/**
 * Dice cast from Sorcery card art. Frozen assets: each entry pairs a recipe
 * with three baked tiles under `public/dice/skins/`, all exported from the
 * dice lab (`valkenhall-assets-lab`, a sibling repo). The lab does the work a
 * player never sees: it crops the art window, pulls a palette, blurs and
 * swirls the art past recognition, bakes the paper tooth and a material
 * grain in, and reads relief and gloss off the result. What lands here is
 * the result, and the client only cuts it into atlases.
 *
 * To add one: run the lab, pick a card, tune, then "Download tiles" into
 * `public/dice/skins/` and paste the copied entry below. Keys are the
 * `id`; the picker shows them after the three paper colours. Nothing
 * experimental belongs in here: whatever is registered at release time
 * ships to every player.
 *
 * Entry shape (see `DICE_PALETTES` in `dieAssets.js` for the paper fields):
 *   id, label, swatch     the picker button
 *   fill                  the rim's tone and the ground before the tile loads
 *   wear                  'r g b' of the darkening toward the edges
 *   tile                  '/game-assets/dice/skins/<id>.webp', the colour
 *   height                '/game-assets/dice/skins/<id>-height.webp', cut into the normal map
 *   roughness             '/game-assets/dice/skins/<id>-roughness.webp', the material's roughness
 *   numeralColor          flat dye for the pencilled numerals
 *   numeralWeight         0..1, extra passes of the pencil (optional)
 *   material              overrides on PAPER_MATERIAL (clearcoat, iridescence, ...)
 *   source                the card slug it was cut from, for the record
 */
export const DICE_SKINS = {
  'avatar-of-air': {
    id: 'avatar-of-air', label: 'Avatar of Air', swatch: '#d3e4e8',
    fill: '#d3e4e8', wear: '20 28 37', numeralColor: '#d3e4e8', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/avatar-of-air.webp',
    height: '/game-assets/dice/skins/avatar-of-air-height.webp',
    roughness: '/game-assets/dice/skins/avatar-of-air-roughness.webp',
    source: 'alp-avatar_of_air-pd-s',
  },
  'avatar-of-earth': {
    id: 'avatar-of-earth', label: 'Avatar of Earth', swatch: '#28261e',
    fill: '#28261e', wear: '12 12 10', numeralColor: '#e5cb90', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/avatar-of-earth.webp',
    height: '/game-assets/dice/skins/avatar-of-earth-height.webp',
    roughness: '/game-assets/dice/skins/avatar-of-earth-roughness.webp',
    source: 'alp-avatar_of_earth-pd-s',
  },
  'avatar-of-fire': {
    id: 'avatar-of-fire', label: 'Avatar of Fire', swatch: '#1d1610',
    fill: '#1d1610', wear: '16 12 9', numeralColor: '#e2bb62', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/avatar-of-fire.webp',
    height: '/game-assets/dice/skins/avatar-of-fire-height.webp',
    roughness: '/game-assets/dice/skins/avatar-of-fire-roughness.webp',
    source: 'alp-avatar_of_fire-pd-s',
  },
  'avatar-of-water': {
    id: 'avatar-of-water', label: 'Avatar of Water', swatch: '#25313e',
    fill: '#25313e', wear: '20 27 34', numeralColor: '#d8dac7', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/avatar-of-water.webp',
    height: '/game-assets/dice/skins/avatar-of-water-height.webp',
    roughness: '/game-assets/dice/skins/avatar-of-water-roughness.webp',
    source: 'alp-avatar_of_water-pd-s',
  },
  'flamecaller': {
    id: 'flamecaller', label: 'Flamecaller', swatch: '#9d3d21',
    fill: '#9d3d21', wear: '30 14 9', numeralColor: '#e6dcc6', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/flamecaller.webp',
    height: '/game-assets/dice/skins/flamecaller-height.webp',
    roughness: '/game-assets/dice/skins/flamecaller-roughness.webp',
    source: 'bet-flamecaller-b-s',
  },
  'geomancer': {
    id: 'geomancer', label: 'Geomancer', swatch: '#c9b160',
    fill: '#c9b160', wear: '12 24 21', numeralColor: '#e6dcc6', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/geomancer.webp',
    height: '/game-assets/dice/skins/geomancer-height.webp',
    roughness: '/game-assets/dice/skins/geomancer-roughness.webp',
    source: 'bet-geomancer-b-s',
  },
  'harbinger': {
    id: 'harbinger', label: 'Harbinger', swatch: '#575971',
    fill: '#575971', wear: '12 8 7', numeralColor: '#c1c4da', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/harbinger.webp',
    height: '/game-assets/dice/skins/harbinger-height.webp',
    roughness: '/game-assets/dice/skins/harbinger-roughness.webp',
    source: 'got-harbinger-pd-s',
  },
  'necromancer': {
    id: 'necromancer', label: 'Necromancer', swatch: '#307e54',
    fill: '#307e54', wear: '7 14 14', numeralColor: '#489f57', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/necromancer.webp',
    height: '/game-assets/dice/skins/necromancer-height.webp',
    roughness: '/game-assets/dice/skins/necromancer-roughness.webp',
    source: 'got-necromancer-pd-s',
  },
  'persecutor': {
    id: 'persecutor', label: 'Persecutor', swatch: '#3b1f1d',
    fill: '#3b1f1d', wear: '13 8 8', numeralColor: '#e55340', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/persecutor.webp',
    height: '/game-assets/dice/skins/persecutor-height.webp',
    roughness: '/game-assets/dice/skins/persecutor-roughness.webp',
    source: 'got-persecutor-pd-s',
  },
  'savior': {
    id: 'savior', label: 'Savior', swatch: '#8f7960',
    fill: '#8f7960', wear: '20 13 14', numeralColor: '#e6dcc6', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/savior.webp',
    height: '/game-assets/dice/skins/savior-height.webp',
    roughness: '/game-assets/dice/skins/savior-roughness.webp',
    source: 'got-savior-pd-s',
  },
  'sparkmage': {
    id: 'sparkmage', label: 'Sparkmage', swatch: '#385cab',
    fill: '#385cab', wear: '15 28 40', numeralColor: '#e6dcc6', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/sparkmage.webp',
    height: '/game-assets/dice/skins/sparkmage-height.webp',
    roughness: '/game-assets/dice/skins/sparkmage-roughness.webp',
    source: 'bet-sparkmage-b-s',
  },
  'waveshaper': {
    id: 'waveshaper', label: 'Waveshaper', swatch: '#35423d',
    fill: '#35423d', wear: '29 36 34', numeralColor: '#e6dcc6', numeralWeight: 0.15,
    tile: '/game-assets/dice/skins/waveshaper.webp',
    height: '/game-assets/dice/skins/waveshaper-height.webp',
    roughness: '/game-assets/dice/skins/waveshaper-roughness.webp',
    source: 'bet-waveshaper-b-s',
  },
};
